import React, { useEffect } from 'react';
import { Row, Col, Card, Table, Button, Tag, Statistic, List, Result } from 'antd';
import { DollarOutlined, FileTextOutlined, DownloadOutlined, LockOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../store/authStore';
import { useCondoStore } from '../../store/condoStore';
import { PaymentForm } from '../../components/payment/PaymentForm';

import { formatDate } from '../../utils/date';
export const OwnerPortal: React.FC = () => {
  const { user, currentCondo } = useAuthStore();
  const { getOwnerDashboard, fetchFees, fetchDocuments, fetchPayments } = useCondoStore();

  // Sprint 36 — findings H1 / H3 / H5. The Owner Portal previously
  // never fetched the data its tiles read from — `currentBalance`
  // came back $0 / monthly-fee fallback, `Available Documents` was
  // hard-coded 0, and the Pending Fees table was empty even when
  // the API had a full ledger. Fetch on mount + when the active
  // condo changes; the existing apiService methods already scope
  // by condoId (Sprints 32/34).
  useEffect(() => {
    if (currentCondo?.id) {
      fetchFees(currentCondo.id);
      fetchDocuments(currentCondo.id);
      fetchPayments(currentCondo.id);
    }
  }, [currentCondo?.id, fetchFees, fetchDocuments, fetchPayments]);

  const isOwnerRole = user?.role === 'owner';

  if (!isOwnerRole) {
    return (
      <Result
        icon={<LockOutlined style={{ color: '#718096' }} />}
        title="Resident Portal"
        subTitle="This section is only available to unit owners. Administrators manage owner accounts from the Owners section of the dashboard."
        style={{ paddingTop: 80 }}
      />
    );
  }

  if (!user?.ownerId) {
    // Sprint 32 — finding R5/R7 tail. The bare "No owner data found"
    // div left owner-role users staring at an empty page when their
    // user row's owner_id column wasn't populated (seeding gap for
    // owner.a@condo.com, but conceivable for any newly-provisioned
    // resident before the admin links their unit). Surface a real
    // help message + the action the user can actually take.
    return (
      <Result
        icon={<FileTextOutlined style={{ color: '#a0aec0' }} />}
        title="Your account isn't linked to a unit yet"
        subTitle="A property manager needs to associate this account with your unit before fees, statements, and documents become visible. Please contact your condo management team."
        style={{ paddingTop: 80 }}
      />
    );
  }
  
  // Sprint 36 — finding H1 + H3. Pass the user UUID (which is what
  // condo.fees.owner_id stores after Sprint 22b.2), not the owners-
  // row bigint string in user.ownerId. Pass currentCondo.id so the
  // dashboard can resolve "documents visible to this resident in
  // their building" properly.
  const dashboard = getOwnerDashboard(user.id, currentCondo?.id);

  const feeColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `$${amount}`,
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: Date) => formatDate(date),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <PaymentForm fee={record} />
      ),
    },
  ];

  const paymentColumns = [
    {
      title: 'Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date: Date) => formatDate(date),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `$${amount}`,
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      render: (method: string) => (
        <Tag>
          {method.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ')}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'completed' ? 'green' : 'orange'}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Owner Portal</h1>
        <p className="text-gray-600">Your condo account overview</p>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Current Balance"
              value={dashboard.currentBalance}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ 
                color: dashboard.currentBalance > 0 ? '#cf1322' : '#3f8600' 
              }}
            />
            {dashboard.currentBalance > 0 && (
              <Button type="primary" size="small" className="mt-2">
                Make Payment
              </Button>
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Pending Fees"
              value={dashboard.pendingFees.length}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Available Documents"
              value={dashboard.availableDocuments.length}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mt-6">
        <Col xs={24} lg={12}>
          <Card title="Pending Fees">
            <Table 
              columns={feeColumns} 
              dataSource={dashboard.pendingFees} 
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Recent Statements">
            <List
              size="small"
              dataSource={dashboard.recentStatements}
              renderItem={(statement) => (
                <List.Item
                  actions={[
                    <Button 
                      key="download" 
                      size="small" 
                      icon={<DownloadOutlined />}
                    >
                      PDF
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={`Statement ${statement.period}`}
                    description={`Generated: ${formatDate(statement.generatedDate)}`}
                  />
                  <Tag color={statement.currentBalance > 0 ? 'red' : 'green'}>
                    ${statement.currentBalance}
                  </Tag>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mt-6">
        <Col xs={24}>
          <Card title="Recent Payments">
            <Table 
              columns={paymentColumns} 
              dataSource={dashboard.recentPayments} 
              rowKey="id"
              pagination={{ pageSize: 5 }}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OwnerPortal;